import { courseSlug } from '../training/courseSlug.js';

/**
 * Old `/training…` learner links → the new `/learning…` pages (training redesign decision 8.4,
 * PLAN §9.3). Links already out in the world — emails, notification deep links, bookmarks, the
 * "Copy link" / WhatsApp shares — keep working because every one of them lands here first.
 *
 *   /training                      → /learning
 *   /training/certificates         → /learning/certificates
 *   /training/courses/:courseId    → /learning/courses/:courseId (still resolved to the caller's own
 *                                     assignment by CourseLinkPage, now handing over to /learning)
 *   /training/:ref                 → /learning/:ref (an assignment id or a course slug)
 *   /training/live/:id/join        → unchanged (null): the live-session join landing stays put
 *   any other /training/live/…     → /learning (there is no learner page under it)
 *
 * Pure, so the table is unit-tested and the route component stays a one-liner.
 */
const LIVE_JOIN = /^\/training\/live\/[^/]+\/join\/?$/;

export function learningPathForTrainingPath(pathname: string): string | null {
  if (pathname !== '/training' && !pathname.startsWith('/training/')) return null;
  if (LIVE_JOIN.test(pathname)) return null;
  const rest = pathname.slice('/training'.length).replace(/\/+$/, '');
  if (rest === '' || rest === '/live' || rest.startsWith('/live/')) return '/learning';
  return `/learning${rest}`;
}

/** The full redirect target, query string and hash carried over (`?item=` deep links). */
export function legacyTrainingRedirect(location: {
  pathname: string;
  search?: string;
  hash?: string;
}): string | null {
  const path = learningPathForTrainingPath(location.pathname);
  if (path === null) return null;
  return `${path}${location.search ?? ''}${location.hash ?? ''}`;
}

/**
 * Is this the course page of the overrunning assignment? The learner's course URL is either the
 * assignment id (old links, notifications) or the course's readable slug, under `/learning` or the
 * old `/training` (for the instant before the redirect lands). The global overrun banner hides
 * there because the player shows its own notice for that course; on every other page — including
 * another course's page — it still shows.
 */
export function isOverrunCoursePage(
  pathname: string,
  overrun: { assignmentId: string; title: string },
): boolean {
  const match = /^\/(?:training|learning)\/([^/]+)\/?$/.exec(pathname);
  const raw = match?.[1];
  if (!raw) return false;
  let ref: string;
  try {
    ref = decodeURIComponent(raw);
  } catch {
    ref = raw;
  }
  return ref === overrun.assignmentId || ref === courseSlug(overrun.title);
}
